import { ApiRequestError } from '@/lib/httpClient'
import { authLog } from './debug'
import type { LoginResponse } from './types'

export type LoginDecision =
  | { kind: 'success' }
  | { kind: 'locked'; message: string }
  | { kind: 'password-reset-required'; message: string }
  | { kind: 'unconfirmed'; message: string }
  | { kind: 'xero-sso'; message: string }
  | { kind: 'verification-sent'; message: string }
  | { kind: 'inactive'; message: string }
  | { kind: 'mfa-required'; session: string; message: string }
  | { kind: 'error'; message: string }

/** What the authenticator-code step does after a failed `mfa/verify`. */
export type MfaVerifyFailure =
  /** Stay on the code step with this fresh challenge session. */
  | { kind: 'mfa-retry'; session: string; message: string }
  /** The challenge can't continue (no fresh session): back to the password step. */
  | { kind: 'mfa-restart'; message: string }
  /** Leave the code step and show this on the password step. */
  | LoginDecision

const LEGACY_INVALID_LOGIN_MESSAGE = 'The login details are incorrect.'

type AuthErrorLike = {
  code?: string
  Code?: string
  message?: string
  Message?: string
  error?: string
  detail?: string
  title?: string
  error_description?: string
}

function normalizeMessage(value: AuthErrorLike | null | undefined, fallback: string) {
  return value?.message || value?.Message || value?.error || value?.detail || value?.title || value?.error_description || fallback
}

function normalizeCode(value: AuthErrorLike | null | undefined) {
  return value?.code || value?.Code
}

export function interpretLoginResponse(response: LoginResponse | null | undefined): LoginDecision {
  if (!response) {
    const decision = { kind: 'error', message: LEGACY_INVALID_LOGIN_MESSAGE } as const
    authLog('interpretLoginResponse: empty response', { response, decision })
    return decision
  }

  const code = normalizeCode(response)
  const message = normalizeMessage(response, LEGACY_INVALID_LOGIN_MESSAGE)

  if (code === 'UserLockedOut' || response.message === 'UserLockedOut') {
    const decision = {
      kind: 'locked',
      message: 'User Account has been locked out due to multiple failed login attempts. Please contact your Admin or support@dashboardinsights.com.',
    } as const
    authLog('interpretLoginResponse: locked user', { response, decision })
    return decision
  }
  
  if (code === 'InactiveUser') {
    const decision = {
      kind: 'inactive',
      message: 'Your account is not active. Please contact your Admin or support@dashboardinsights.com.',
    } as const
    authLog('interpretLoginResponse: inactive user', { response, decision })
    return decision
  }

  if (code === 'MfaRequired') {
    if (!response.session) {
      const decision = { kind: 'error', message: LEGACY_INVALID_LOGIN_MESSAGE } as const
      authLog('interpretLoginResponse: MfaRequired without a session', { decision })
      return decision
    }
    const decision = {
      kind: 'mfa-required',
      session: response.session,
      // Shown by callers that have no code step (signup's login step).
      message:
        "This account signs in with an authenticator app code, which this step doesn't support yet. Please contact support@dashboardinsights.com.",
    } as const
    authLog('interpretLoginResponse: authenticator code required', { kind: decision.kind })
    return decision
  }

  if (code === 'PasswordResetRequiredException') {
    const decision = {
      kind: 'password-reset-required',
      message: 'A password reset is required before you can sign in.',
    } as const
    authLog('interpretLoginResponse: password reset required', { response, decision })
    return decision
  }

  if (code === 'UserNotConfirmedException') {
    const decision = {
      kind: 'unconfirmed',
      message: 'Your account is not yet verified. Please follow the verification email instructions and then sign in again.',
    } as const
    authLog('interpretLoginResponse: unconfirmed user', { response, decision })
    return decision
  }

  if (code === 'XeroSSOUser') {
    const decision = {
      kind: 'xero-sso',
      message: response.message ?? 'This account uses Xero sign-in. Use the "Sign in with Xero" option below.',
    } as const
    authLog('interpretLoginResponse: xero sso user', { response, decision })
    return decision
  }

  if (response.message === 'EmailPosted') {
    const decision = {
      kind: 'verification-sent',
      message: 'Verification email sent. Complete email verification and login again.',
    } as const
    authLog('interpretLoginResponse: verification email posted', { response, decision })
    return decision
  }

  if (response.idToken && response.refreshToken && response.expiry != null) {
    const decision = { kind: 'success' } as const
    authLog('interpretLoginResponse: success token payload', {
      hasIdToken: Boolean(response.idToken),
      hasRefreshToken: Boolean(response.refreshToken),
      hasExpiry: response.expiry != null,
      decision,
    })
    return decision
  }

  const decision = { kind: 'error', message: LEGACY_INVALID_LOGIN_MESSAGE } as const
  authLog('interpretLoginResponse: unmapped response fell back to legacy invalid credentials', {
    response,
    normalizedMessage: message,
    decision,
  })
  return decision
}

export const MFA_CODE_MISMATCH_MESSAGE = "That code isn't right. Check your authenticator app and try again."
export const MFA_CODE_EXPIRED_MESSAGE = 'That took a little too long. Enter the current code from your authenticator app.'
export const MFA_SIGN_IN_AGAIN_MESSAGE = 'Your sign-in timed out. Please sign in again.'
export const MFA_VERIFY_FAILED_MESSAGE = "We couldn't check that code. Please try again."

export function interpretMfaVerifyError(error: unknown): MfaVerifyFailure {
  if (error instanceof ApiRequestError && error.status !== 429) {
    const payload = (error.payload || {}) as AuthErrorLike & { session?: string }
    const code = error.code || normalizeCode(payload)
    if (code === 'MfaCodeMismatch' || code === 'MfaSessionExpired') {
      // No fresh session means the sign-in can't continue from here (e.g. the password changed meanwhile).
      if (!payload.session) return { kind: 'mfa-restart', message: MFA_SIGN_IN_AGAIN_MESSAGE }
      return {
        kind: 'mfa-retry',
        session: payload.session,
        message: code === 'MfaCodeMismatch' ? MFA_CODE_MISMATCH_MESSAGE : MFA_CODE_EXPIRED_MESSAGE,
      }
    }

    const decision = interpretAuthError(error)
    // Any other 401 means this sign-in can't go on (e.g. the password changed meanwhile).
    if (error.status === 401 && decision.kind === 'error') return { kind: 'mfa-restart', message: decision.message }
    // Server or network trouble: the challenge may still be valid, so the user stays on the code step.
    if (error.status >= 500) return { kind: 'error', message: MFA_VERIFY_FAILED_MESSAGE }
    return decision
  }
  if (!(error instanceof ApiRequestError)) return { kind: 'error', message: MFA_VERIFY_FAILED_MESSAGE }
  return interpretAuthError(error)
}

export const EMAIL_LINK_EXPIRED_MESSAGE = 'This sign-in link has expired or was already used. Please sign in again.'
export const EMAIL_LINK_RETRY_MESSAGE = "We couldn't sign you in right now. Please try again."

/** What the email-link page shows after a failed confirm, and whether trying again can help. */
export function interpretEmailLinkError(error: unknown): { message: string; canRetry: boolean } {
  if (!(error instanceof ApiRequestError) || error.status >= 500) {
    return { message: EMAIL_LINK_RETRY_MESSAGE, canRetry: true }
  }
  if (error.status === 429) {
    return { message: 'Too many attempts. Please try again in a few minutes.', canRetry: true }
  }
  const decision = interpretAuthError(error)
  if (decision.kind === 'locked' || decision.kind === 'inactive') {
    return { message: decision.message, canRetry: false }
  }
  // LinkExpired, or anything else the API refuses: the link can't be used again.
  return { message: EMAIL_LINK_EXPIRED_MESSAGE, canRetry: false }
}

export function interpretAuthError(error: unknown): LoginDecision {
  if (error instanceof ApiRequestError) {
    const payload = (error.payload || {}) as AuthErrorLike
    const code = error.code || normalizeCode(payload)
    const message = normalizeMessage(payload, error.message || LEGACY_INVALID_LOGIN_MESSAGE)
    authLog('interpretAuthError: ApiRequestError', {
      status: error.status,
      code,
      message,
      payload,
    })
    
    if (error.status === 429) {
      return {
        kind: 'error',
        message: 'Too many login attempts. Please try again later.',
      } as const
    }

    return interpretLoginResponse({
      code,
      message,
      error: payload.error,
    })
  }

  if (error && typeof error === 'object') {
    const payload = error as AuthErrorLike
    authLog('interpretAuthError: object error', { payload })
    return interpretLoginResponse({
      code: normalizeCode(payload),
      message: normalizeMessage(payload, LEGACY_INVALID_LOGIN_MESSAGE),
      error: payload.error,
    })
  }

  const decision = {
    kind: 'error',
    message: LEGACY_INVALID_LOGIN_MESSAGE,
  } as const
  authLog('interpretAuthError: primitive/unknown error fell back to legacy invalid credentials', {
    error,
    decision,
  })
  return decision
}
